import React from "react";
import { useRouteMatch, Link, useLocation, useHistory } from "react-router-dom";
import { useAuthState } from "../../../components/AuthProvider";
import CompareRecords from "../../NewSearchRecords/SearchRecordTable/CompareRecords";
import fetchConfig from "../../../utils/fetchConfig";
import handleResponse from "../../../utils/handleFetchResponse";
import { useMessageToast } from "../../../components/MessageProvider";
import Button from "../../../components/Button";
import Spinner from "../../../components/Loader/Spinner";

import styles from "./QuarantineReviewQueue.module.scss";
import {
  fetchReviewQueueCountData,
  fetchBulkOrgData,
  fetchClientSubscriptionsData,
  aggregatesCall
} from "../../../utils/fetchData";

function QuarantineReviewQueue() {
  const { user } = useAuthState();
  let { path, url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  const {
    selectedClient: { oid: clientId }
  } = user.preferences;
  const userId = user.username;
  const toast = useMessageToast();

  const [
    OverlayRecordLoadingState,
    setQuarantineRecordLoadingState
  ] = React.useState(false);
  const [quarantineData, setQuarantineData] = React.useState([]);
  const [quarantineRecordId, setQuarantineRecordId] = React.useState("");
  const [
    quarantineRecordSourceSystem,
    setQuarantineRecordSourceSystem
  ] = React.useState("");

  const [isRecordOverridden, setIsRecordOverridden] = React.useState(false);

  const [disableApplyChanges, setDisableApplyChanges] = React.useState(true);

  const constructQuarantineRecordsData = React.useCallback(
    quarantineRecordsData => {
      const currentRecordDemographics = JSON.parse(
        quarantineRecordsData.currentRecord.demographics
      );
      const quarantineRecordDemographics = JSON.parse(
        quarantineRecordsData.quarantineRecord.demographics
      );
      setQuarantineData([
        currentRecordDemographics,
        quarantineRecordDemographics
      ]);
    },
    []
  );

  const fetchQuarantineRecord = React.useCallback(() => {
    setQuarantineRecordLoadingState(true);
    let url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/quarantine/idspecQuarantinedRecord`;
    if (location.state.sourceEntityId && location.state.sourceSystemId) {
      url =
        url +
        "?sourceEntityId=" +
        location.state.sourceEntityId +
        "&sourceSystemId=" +
        location.state.sourceSystemId;
    }
    url = encodeURI(url);
    const mockUrl = `/QuarantineRecord.json`;
    const config = {
      method: "GET",
      credentials: "include",
      headers: {
        clientId: clientId,
        userId: userId
      }
    };
    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        let isrestricted = response.isRestricted == "true" ? true : false;
        let restrictedeids = response.RestrictedEids;
        if (
          isrestricted &&
          restrictedeids != "null" &&
          restrictedeids.length > 0
        ) {
          restrictedeids = restrictedeids
            .replaceAll(/Patient\/|\[|\]/gi, "")
            .split("/");
          toast.info(
            "You are not authorized to view Quarantine record " +
              restrictedeids[0] +
              " due to data governance restriction/compliance."
          );
          history.push("/review-queue-dashboard");
        }
        setQuarantineRecordLoadingState(false);
        if (response.data) {
          setQuarantineRecordId(response.data.quarantineRecord.sourceEntityId);
          setQuarantineRecordSourceSystem(
            response.data.quarantineRecord.sourceSystem
          );
          constructQuarantineRecordsData(response.data);
        }
      })
      .catch(error => {
        console.log("fetchQuarantineRecord error: ", error);
        setQuarantineRecordLoadingState(false);
        if (
          error &&
          error.toString() &&
          error.toString().includes("SyntaxError: Unexpected token")
        ) {
          toast.error("Quarantine Record not found");
        } else {
          toast.error("Error in fetching Quarantine Record");
        }
        history.push("/review-queue-dashboard");
      });
  }, [toast, constructQuarantineRecordsData, clientId, userId]);

  const recordUnquarantineHandler = type => {
    var currentQuarantineValue;
    if (type === "preOverlay") {
      currentQuarantineValue = quarantineData[0];
      setDisableApplyChanges(true);
    } else {
      currentQuarantineValue = quarantineData[1];
      setDisableApplyChanges(false);
    }
    const currentRecordValue = quarantineData[0];
    const quarantineRecordValue = quarantineData[1];

    setQuarantineData([currentRecordValue, quarantineRecordValue]);
    setIsRecordOverridden(!isRecordOverridden);
  };
  const { selectedClient } = user.preferences;
  const redirectToFocusIdentity = (Record_ID, Source_System) => {
    setQuarantineRecordLoadingState(true);
    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/aggregates`;
    const mockUrl = `/fhirPatients.json`;
    const config = {
      method: "POST",
      body: JSON.stringify({
        ids: [{ value: Record_ID }],
        sourceId: Source_System
      }),
      credentials: "include",
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json"
      }
    };

    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        setQuarantineRecordLoadingState(false);
        history.push({
          pathname: `/search-identities-new/${clientId}/view/${response.data[0].id}/`,
          state: {
            preLoadedSearchCriteria: [
              {
                key: "golden_id",
                label: "HCM ID/EID",
                value: response.data[0].id
              }
            ]
          }
        });
      })
      .catch(error => {
        setQuarantineRecordLoadingState(false);
        toast.error("Focus Identity not found");
      });
  };

  const focusIdentityButtonHandler = (Record_ID, Source_System) => {
    return (
      <Button
        className={styles.nextArrowIcon}
        onClick={() => redirectToFocusIdentity(Record_ID, Source_System)}
      >
        Focus Identity
      </Button>
    );
  };

  const applyOrReviewQuarantineChangesHandler = (
    currentQuarantineRecordDemographics,
    status,
    fromSearchOverlay
  ) => {
    let unquarantine_request = JSON.parse(
      JSON.stringify(currentQuarantineRecordDemographics)
    );
    if (unquarantine_request.meta.lastUpdated) {
      delete unquarantine_request.meta.lastUpdated;
    }

    setQuarantineRecordLoadingState(true);
    const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patientsource/unquarantine?quarantineStatus=${status}`;
    const mockUrl = `/QuarantineRecord.json`;
    const config = {
      method: "PUT",
      body: JSON.stringify(unquarantine_request),
      credentials: "include",
      headers: {
        clientId: clientId,
        userId: userId,
        "Content-Type": "application/json",
        sourceId: quarantineRecordSourceSystem,
        sourceEntityId: quarantineRecordId
      }
    };
    fetch(...fetchConfig(url, mockUrl, config))
      .then(handleResponse)
      .then(response => {
        setQuarantineRecordLoadingState(false);
        if (status == "closed") {
          toast.success(`Review Completed`);
        }
        redirectToFocusIdentity(
          currentQuarantineRecordDemographics.id,
          currentQuarantineRecordDemographics.meta.source
        );
      })
      .catch(error => {
        console.log("applyQuarantineChangesHandler error: ", error);
        setQuarantineRecordLoadingState(false);
        toast.error("Error occured while applying changes");
      });
  };

  React.useEffect(() => {
    fetchQuarantineRecord();
  }, [fetchQuarantineRecord]);

  return (
    <div>
      {OverlayRecordLoadingState ? (
        <Spinner />
      ) : (
        quarantineData.length &&
        quarantineRecordSourceSystem.length > 0 &&
        quarantineRecordId.length > 0 && (
          <CompareRecords
            overlayRecordsData={quarantineData}
            isQuarantineRecord={true}
            clientId={clientId}
            recordOverrideHandler={recordUnquarantineHandler}
            isRecordOverridden={isRecordOverridden}
            focusIdentityButtonHandler={focusIdentityButtonHandler}
            applyAndResolveOverlayRecordHandler={
              applyOrReviewQuarantineChangesHandler
            }
            skipAndReviewHandler={applyOrReviewQuarantineChangesHandler}
            fromSearchOverlay={location.state.searchOverlay}
            disableApplyChanges={disableApplyChanges}
          />
        )
      )}
    </div>
  );
}

export default QuarantineReviewQueue;
